"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import styles from "./navBar.module.css";
import {
    AppBar,
    Toolbar,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    createTheme,
    Tooltip,
} from "@mui/material";
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import keyChoiceLendingLogo from "../../src/assets/icons/keyChoiceLendingLogo.webp";
import keyChoiceLendingLogoWhite from "../../src/assets/icons/keyChoiceLendingLogo.webpWhite.webp";
import australiaFlag from "../../src/assets/icons/australiaFlag.svg";
import vietnamFlag from "../../src/assets/icons/vietnamFlag.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { useRouter, usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';

const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
});

const NavBar = () => {
    const pathname = usePathname();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const [searchClicked, setSearchClicked] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const router = useRouter();
    const [selectedCountry, setSelectedCountry] = useState("australia");
    const [searchInputValue, setSearchInputValue] = useState("");
    const { i18n } = useTranslation();
    const { t } = useTranslation();

    useEffect(() => {
        setMobileOpen(false);
    }, [isMobile]);

    // Define routes that should have white background, black text, and regular logo
    const specialRoutes = [
        '/home-loan-for-your-dream-home/',
        '/the-power-of-refinancing/',
        '/reverse-mortgage/',
        '/unlocking-doors-through-investment-lending/',
        '/expat-finance-simplified-for-australian-property/',
        '/finance-for-business-growth/',
        '/financing-smsf-property-investment/',
        '/commercial-loans/',
        '/videos/',
        '/faq/',
        '/mortgage-broker-malvern/',
        '/leading-coffs-harbour-finance-brokers/',
        '/search/',
        '/blogs/transform-your-retirement-with-home-equity-release/',
        '/blogs/getting-the-basis-of-advice-right/',
        '/blogs/exploring-the-lenders/',
        '/blogs/demystifying-business-finance-jargon/',
        '/blogs/navigating-uncertain-times/',
        '/blogs/debt-payment-in-full/',
        '/blogs/understanding-ratios-in-commercial-lending/',
        '/blogs/assessing-borrowing-capacity/',
        '/blogs/unlocking-growth/',
        '/blogs/comprehensive-loan-documentation-checklist/',
        '/blogs/empowering-your-business-growth/',
        '/blogs/exploring-different-loan-types/',
        '/blogs/mastering-commercial-lending/',
        '/blogs/the-importance-of-guarantees-and-indemnities/',
        '/blogs/understanding-interest-rates-in-commercial-finance/',
        '/blogs/understanding-property-security/',
        '/blogs/unlocking-growth/',
        '/blogs/unlocking-business-growth/',
        '/blogs/business-loan-readiness-assessment-unlocking-your-businesss-potential-through-comprehensive-discovery-and-needs-analysis/',
        '/blogs/business-lending-understanding-serviceability-and-covenant-ratios/',
        '/blogs/a-guide-to-ratios-and-financial-analysis-to-understanding-business-lending-security-types/',
        '/blogs/business-lending-types-of-security-for-securing-finance/',
        '/blogs/business-lending-loan-readiness-assessment-needs-analysis-2/',
        '/blogs/overview-of-business-lending-in-a-changing-landscape/',
        '/blogs/understanding-ratios-in-commercial-lending-and-their-significance/',
        '/blogs/understanding-loan-repayment-structures-and-interest-rates-in-business-lending-2/',
        '/blogs/key-factors-in-business-lending-security',
        '/blogs/key-factors-in-business-lending-security/',
        '/blogs/checklist-smooth-application-process-for-business-finance/',
        '/blogs/business-finance-structuring-factors-for-informed-advice/',
        '/blogs/business-lending-exploring-different-loan-types-variable-rate-vs-fixed-rate/',
        '/blogs/serviceability-and-key-measures-for-business-borrowers/',
        '/blogs/business-lending-safeguarding-borrowers-and-lenders-in-financial-transactions/',
        '/blogs/unlocking-growth-equipment-as-collateral-for-financing/',
        '/blogs/benefits-of-a-business-loan/',
        '/blogs/mastering-commercial-lending-repayment-structures-loan-terms-2/',
        '/blogs/interest-rates-in-commercial-finance/',
        '/blogs/what-is-asset-finance/',
        '/blogs/fixed-interest-rate-to-variable/',
        '/blogs/offset-accounts-and-redraw-facilities/',
        '/blogs/split-loans/',
        '/blogs/fixed-rate-home-loans/',
        '/blogs/debt-consolidation/',
        '/blogs/variable-rate-loans/',
        '/blogs/first-home-buyers-family-pledge-gurantee/',
        '/blogs/split-loans/',
        '/blogs/lenders-mortgage-insurance',
        '/blogs/credit-cards/',
        '/blogs/interest-only-loans-and-repayment-types/',
        '/blogs/how-much-does-it-really-cost-to-buy-property/',
        '/blogs/business-lending-loan-readiness-assessment-needs-analysis/',
        '/blogs/beginners-guide-to-deposit-bonds/',
        '/blogs/property-investment-guide/',
        '/blogs/self-employed-home-loan/',
        '/blogs/downsizers-deposit-bond/',
        '/blogs/first-home-buyers-deposit-bond/',
        '/blogs/deposit-bond-questions/',
        '/blogs/fixed-rate-loans-mortgage/',
        '/bolgs/covid-pandemic-financing/',
        '/blogs/debt-consolidation-refinancing/',
        '/blogs/fixed-rate-loans-guide/',
        '/blogs/home-deposit-saving-tips/',
        '/blogs/home-buyer-essentials/',
        '/blogs/first-time-home-buyer-tips/',
        '/blogs/covid-pandemic-financing/',
        '/blogs/business-lending-safeguarding-borrowers-and-lenders-in-financial-transactions-2/',
        '/blogs/unlock-business-growth-with-cash-flow-lending/',
        '/blogs/bad-credit-rating/',
        '/blogs/home-loan-essentials/',
        '/blogs/lenders-mortgage-insurance/',
        '/blogs/category/e-book/',
        '/blogs/category/new-blogs/',
        '/blogs/mastering-commercial-lending-repayment-structures-loan-terms/',
        '/blogs/business-lending-exploring-different-loan-types-variable-rate-vs-fixed-rate-2/',
        '/blogs/category/fact-sheets/',
        '/blogs/category/insights/',
        '/blogs/category/retirees-equity-release/',
        '/smsf-loan/',
        '/blogs/first-home-buyers-family-pledge-guarantee/',
        '/blogs/',
        '/blogs/author/keychoice/',
        '/commercial-finance-loan/',
        '/reverse-mortgage-loan/',
        '/smsf-loan/index.html',
        '/commercial-finance-loan/index.html',
        '/reverse-mortgage-loan/index.html'

    ];
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;
            const scrollThreshold = windowHeight * 0.1;

            setIsScrolled(scrollPosition > scrollThreshold);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const savedLang = localStorage.getItem('language') || 'en';
        if (savedLang && i18n.language !== savedLang) {
            i18n.changeLanguage(savedLang);
        }
        setSelectedCountry(savedLang === 'en' ? 'australia' : 'vietnam');
    }, [i18n]);

    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'language' && e.newValue) {
                const newLang = e.newValue;
                i18n.changeLanguage(newLang);
                setSelectedCountry(newLang === 'en' ? 'australia' : 'vietnam');
            }
        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [i18n]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    const handleMobileSubmenuToggle = (menu) => {
        setMobileSubmenuOpen(mobileSubmenuOpen === menu ? null : menu);
    };
    const handleMouseEnter = (menu) => {
        setDropdownOpen(menu);
        setHoveredItem(menu);
    };
    const handleAboutClick = () => {
        router.push('/about');
    };
    const handleMouseLeave = (menu) => {
        const dropdownElement = document.querySelector(
            menu === "solutions"
                ? `.${styles.dropdownSolutions}`
                : `.${styles.dropdown}`
        );
        if (dropdownElement) {
            const leaveTimeout = setTimeout(() => {
                const isMouseOver = dropdownElement.matches(":hover");
                if (!isMouseOver) {
                    setDropdownOpen(null);
                    setHoveredItem(null);
                }
            }, 100);
            dropdownElement.leaveTimeout = leaveTimeout;
        }
    };
    const handleDropdownMouseEnter = (menu) => {
        const dropdownElement = document.querySelector(
            menu === "solutions"
                ? `.${styles.dropdownSolutions}`
                : `.${styles.dropdown}`
        );
        if (dropdownElement && dropdownElement.leaveTimeout) {
            clearTimeout(dropdownElement.leaveTimeout);
        }
    };
    const navLinks = [
        { title: "Home", path: "/" },
        { title: "Services", path: "/services" },
        { title: "About", path: "/about" },
        { title: "Contact", path: "/contact" },
        { title: "Loan Calculators", path: "/loan-calculators" },
    ];
    const drawer = (
        <List>
            {navLinks.map((link) => (
                <ListItem
                    key={link.path}
                    component={Link}
                    href={link.path}
                    onClick={handleDrawerToggle}
                >
                    <ListItemText primary={link.title} />
                </ListItem>
            ))}
        </List>
    );
    const handleSearchSubmit = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            const trimmedInput = searchInputValue.trim();
            const query = trimmedInput ? `?q=${encodeURIComponent(trimmedInput)}` : "";
            router.push(`/search${query}`);
            setSearchClicked(false);
            setSearchInputValue("");
        }
    };
    const handleCountryChange = (event) => {
        const newCountry = event.target.value;
        const newLanguage = newCountry === 'australia' ? 'en' : 'vi';

        setSelectedCountry(newCountry);
        i18n.changeLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);
    };
    const getLogoSource = () => {
        if (isMobile) {
            return keyChoiceLendingLogo;
        }
        if (specialRoutes.includes(pathname)) {
            return keyChoiceLendingLogo;
        }
        return isScrolled ? keyChoiceLendingLogo : keyChoiceLendingLogoWhite;
    };
    const getBackgroundColor = () => {
        if (isMobile) {
            return "rgb(255, 255, 255)";
        }
        if (specialRoutes.includes(pathname)) {
            return "rgb(255, 255, 255)";
        }
        return isScrolled ? "rgb(255, 255, 255)" : "transparent";
    };
    const getTextColor = () => {
        if (isMobile) {
            return "#000000";
        }
        if (specialRoutes.includes(pathname)) {
            return "#000000";
        }
        return isScrolled ? "#000000" : "#ffffff";
    };
    const getLinkColor = (linkName) => {
        if (isMobile) {
            return "#000000";
        }
        if (specialRoutes.includes(pathname)) {
            if (hoveredItem && hoveredItem !== linkName) {
                return "#808080"; // Grey color for non-hovered links
            }
            return "#000000";
        }
        if (isScrolled) {
            if (hoveredItem && hoveredItem !== linkName) {
                return "#808080"; // Grey color for non-hovered links
            }
            return "#000000";
        }
        if (hoveredItem && hoveredItem !== linkName) {
            return "#808080"; // Grey color for non-hovered links
        }
        return "#ffffff";
    };
    const getLinkHoverColor = () => {
        if (isMobile) {
            return "#33A8E4";
        }
        if (specialRoutes.includes(pathname)) {
            return "#33A8E4";
        }
        return isScrolled ? "#33A8E4" : "#ffffff";
    };
    // Function to check if a path is active
    const isActivePath = (path) => {
        // Normalize paths by removing trailing slashes for comparison
        const normalizedPath = path.replace(/\/+$/, '');
        const normalizedPathname = pathname.replace(/\/+$/, '');

        if (normalizedPath === '') {
            return normalizedPathname === '';
        }

        // Special handling for nested routes
        if (normalizedPath === '/solutions') {
            return normalizedPathname.startsWith('/solutions') ||
                normalizedPathname.startsWith('/home-loan-for-your-dream-home') ||
                normalizedPathname.startsWith('/the-power-of-refinancing') ||
                normalizedPathname.startsWith('/reverse-mortgage') ||
                normalizedPathname.startsWith('/unlocking-doors-through-investment-lending') ||
                normalizedPathname.startsWith('/expat-finance-simplified-for-australian-property') ||
                normalizedPathname.startsWith('/finance-for-business-growth') ||
                normalizedPathname.startsWith('/financing-smsf-property-investment');
        }
        if (normalizedPath === '/locations') {
            return normalizedPathname.startsWith('/mortgage-broker-malvern') ||
                normalizedPathname.startsWith('/leading-coffs-harbour-finance-brokers');
        }
        if (normalizedPath === '/loan-calculators') {
            return normalizedPathname.startsWith('/loan-calculators');
        }
        if (normalizedPath === '/resources') {
            return normalizedPathname.startsWith('/videos') ||
                normalizedPathname.startsWith('/blogs') ||
                normalizedPathname.startsWith('/faq');
        }
        if (normalizedPath === '/about') {
            return normalizedPathname.startsWith('/about');
        }
        if (normalizedPath === '/contact') {
            return normalizedPathname.startsWith('/contact');
        }
        return normalizedPathname === normalizedPath;
    };

    // Function to check if a submenu item is active
    const isSubmenuActive = (path) => {
        const normalizedPath = path.replace(/\/+$/, '');
        const normalizedPathname = pathname.replace(/\/+$/, '');
        return normalizedPathname === normalizedPath;
    };

    // Function to get active class
    const getActiveClass = (path) => {
        return isActivePath(path) ? styles.active : '';
    };

    // Function to get submenu active class
    const getSubmenuActiveClass = (path) => {
        return isSubmenuActive(path) ? styles.active : '';
    };
    return (

        <AppBar
            position="fixed"
            sx={{
                backgroundColor: getBackgroundColor(),
                color: getTextColor(),
                padding: isMobile ? "0" : "1rem 0 1rem 0",
                transition: "background-color 0.3s ease, color 0.3s ease",
            }}
        >
            <div className="container">
                {!isMobile && (
                    <Toolbar
                        disableGutters
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            py: 1,
                        }}
                    >
                        {!isMobile ? (
                            <Box className={styles.headerSocials}>
                                <nav aria-label="Social media links">
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', gap: '24px' }}>
                                        <li style={{ display: 'flex' }}>
                                            <a href="https://www.instagram.com/keychoicelending/" className={styles.navbarSocial} aria-label="Visit us on Instagram">
                                                <InstagramIcon
                                                    className={styles.socialLogo}
                                                    sx={{
                                                        color: getTextColor(),
                                                        fontSize: 23,
                                                    }}
                                                />
                                            </a>
                                        </li>
                                        <li style={{ display: 'flex' }}>
                                            <a href="https://www.facebook.com/KeyChoiceLending/" className={styles.navbarSocial} aria-label="Visit us on facebook">
                                                <FacebookIcon
                                                    className={styles.socialLogo}
                                                    sx={{
                                                        color: getTextColor(),
                                                        fontSize: 23,
                                                    }}
                                                />
                                            </a>
                                        </li>
                                        <li style={{ display: 'flex' }}>
                                            <a href="https://www.linkedin.com/company/keychoicelending/" className={styles.navbarSocial} aria-label="Visit us on LinkedIn">
                                                <LinkedInIcon
                                                    className={styles.socialLogo}
                                                    sx={{
                                                        color: getTextColor(),
                                                        fontSize: 23,
                                                    }}
                                                />
                                            </a>
                                        </li>
                                    </ul>
                                </nav>
                            </Box>
                        ) : null}

                        {!isMobile ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                    position: "relative",
                                }}
                            >
                                {!searchClicked ? (
                                    <SearchIcon
                                        className={styles.searchIcon}
                                        onClick={() => setSearchClicked(true)}
                                        sx={{ color: getTextColor() }}
                                    />
                                ) : (
                                    <Box className={`${styles.closeDropdown} ${styles.active}`}>
                                        <CloseIcon
                                            className={styles.closeIcon}
                                            onClick={() => {
                                                setSearchClicked(false);
                                                setSearchInputValue("");
                                            }}
                                        />
                                        <Box className={styles.searchBar}>
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchInputValue}
                                                onChange={(e) => setSearchInputValue(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                                            />
                                            <SearchIcon
                                                className={styles.searchIcon}
                                                onClick={(e) => handleSearchSubmit(e)}
                                                sx={{ color: "black" }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                <Box className={styles.navbarPhoneTimmings}>
                                    <Box
                                        sx={{
                                            textAlign: "right",
                                            display: "flex",
                                            gap: 4,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 2,
                                                fontSize: 12,
                                                fontWeight: "bold",
                                                color: getTextColor(),
                                            }}
                                        >
                                            <AccessTimeIcon
                                                className={styles.mui_icons}
                                                sx={{ color: getTextColor() }}
                                            />{" "}
                                            {t('Mon - Fri: 09:00 - 18:00')}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        position: "relative",
                                        cursor: "pointer",
                                    }}
                                    onMouseEnter={() => setDropdownOpen("country")}
                                    onMouseLeave={() => setDropdownOpen(null)}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            fontSize: 12,
                                            fontWeight: "bold",
                                            color: getTextColor(),
                                        }}
                                    >
                                        <Image
                                            src={selectedCountry === "australia" ? australiaFlag : vietnamFlag}
                                            width={20}
                                            height={20}
                                            alt={`${selectedCountry === "australia" ? "Australia" : "Tieng Viet"} Flag`}
                                            aria-hidden="false"
                                            role="img"
                                        />{" "}
                                        {selectedCountry === "australia" ? "Australia" : "Tieng Viet"}
                                        <KeyboardArrowDownIcon
                                            sx={{
                                                fontSize: 16,
                                                transition: "transform 0.3s",
                                                transform: dropdownOpen === "country" ? "rotate(180deg)" : "none",
                                                color: getTextColor(),
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    </Typography>
                                    {dropdownOpen === "country" && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: "100%",
                                                right: 0,
                                                backgroundColor: "#ffffff",
                                                borderRadius: "4px",
                                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                zIndex: 1000,
                                                minWidth: "150px",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(0,0,0,0.05)",
                                                    },
                                                }}
                                                onClick={() => {
                                                    handleCountryChange({ target: { value: "australia" } });
                                                    setDropdownOpen(null);
                                                }}
                                            >
                                                <Image
                                                    src={australiaFlag}
                                                    width={20}
                                                    height={20}
                                                    alt="Australia Flag"
                                                    aria-hidden="false"
                                                    role="img"
                                                />
                                                <Typography
                                                    className={selectedCountry === "australia" ? styles.active : ""}
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: "bold",
                                                        color: "#000000",
                                                    }}
                                                >
                                                    Australia
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    padding: "8px 12px",
                                                    cursor: "pointer",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(0,0,0,0.05)",
                                                    },
                                                }}
                                                onClick={() => {
                                                    handleCountryChange({ target: { value: "vietnam" } });
                                                    setDropdownOpen(null);
                                                }}
                                            >
                                                <Image
                                                    src={vietnamFlag}
                                                    width={20}
                                                    height={20}
                                                    alt="Tiếng Việt Flag"
                                                    aria-hidden="false"
                                                    role="img"
                                                />
                                                <Typography
                                                    className={selectedCountry === "vietnam" ? styles.active : ""}
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: "bold",
                                                        color: "#000000",
                                                    }}
                                                >
                                                    Tiếng Việt
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ) : null}
                    </Toolbar>
                )}
            </div>

            {/* <Box
                    sx={{
                        borderBottom: "1px solid rgb(210, 210, 210)",
                        width: "100%",
                        display: isMobile ? "none" : "block"
                    }}
                /> */}
            <div className="container">
                <Box sx={{ marginTop: "1rem" }}>
                    {!isMobile ? (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "space-between",
                                gap: 3,
                                justifyContent: "space-between",
                            }}
                        >
                            <Link href="/" passHref>
                                <Image
                                    src={getLogoSource()}
                                    width={140}
                                    height={45}
                                    alt="keyChoiceLendingLogo"
                                    aria-hidden="false"
                                    role="img"
                                />
                            </Link>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                }}
                            >
                                <nav aria-label="Main navigation">
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', gap: '24px', alignItems: 'center' }}>
                                        <li style={{ display: 'flex' }}>
                                            <Box
                                                sx={{ position: "relative" }}
                                                onMouseEnter={() =>
                                                    handleMouseEnter("solutions")
                                                }
                                                onMouseLeave={() =>
                                                    handleMouseLeave("solutions")
                                                }
                                            >
                                                <Link href="/solutions" passHref>
                                                    <Tooltip
                                                        title={t('Solutions')}
                                                        arrow
                                                        componentsProps={{
                                                            tooltip: {
                                                                sx: {
                                                                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                                                                    fontSize: '1.5rem',
                                                                    padding: '0.75rem 1.5rem',
                                                                    '& .MuiTooltip-arrow': {
                                                                        color: 'rgba(0, 0, 0, 0.8)',
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                // gap: 1,
                                                                fontSize: 14,
                                                                fontWeight: 600,
                                                            }}
                                                            className={`${hoveredItem === "solutions"
                                                                ? styles.hovered
                                                                : hoveredItem
                                                                    ? styles.lighter
                                                                    : ""
                                                                } ${isScrolled
                                                                    ? styles.scrolled
                                                                    : styles.notScrolled
                                                                } ${getActiveClass('/solutions')}`}
                                                            onMouseEnter={() =>
                                                                setHoveredItem("solutions")
                                                            }
                                                            onMouseLeave={() =>
                                                                setHoveredItem(null)
                                                            }
                                                            style={{
                                                                color: getLinkColor("solutions"),
                                                                '&:hover': {
                                                                    color: getLinkHoverColor()
                                                                }
                                                            }}
                                                        >
                                                            {t('Solutions')} <KeyboardArrowDownIcon sx={{ color: getLinkColor("solutions"), height: "2.25rem", width: "2.25rem" }} />
                                                        </Typography>
                                                    </Tooltip>
                                                </Link>
                                                {dropdownOpen === "solutions" && (
                                                    <Box
                                                        className={`${styles.dropdownSolutions}`}
                                                        onMouseEnter={() =>
                                                            handleDropdownMouseEnter(
                                                                "solutions"
                                                            )
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                styles.dropdownColumn
                                                            }
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                    marginBottom: "6px",
                                                                    color: "#33A8E4",
                                                                }}
                                                            >
                                                                {t('Buy or Refinance a Home')}
                                                            </Typography>
                                                            <Link href="/home-loan-for-your-dream-home" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/home-loan-for-your-dream-home')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Home Loans')}
                                                                </Typography>
                                                            </Link>
                                                            <Link href="/the-power-of-refinancing" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/the-power-of-refinancing')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Refinance')}
                                                                </Typography>
                                                            </Link>
                                                            <Link href="/reverse-mortgage" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/reverse-mortgage')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Reverse Mortgage')}
                                                                </Typography>
                                                            </Link>
                                                        </Box>
                                                        <Box
                                                            className={
                                                                styles.dropdownColumn
                                                            }
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                    marginBottom: "6px",
                                                                    color: "#33A8E4",
                                                                }}
                                                            >
                                                                {t('Invest in Property')}
                                                            </Typography>
                                                            <Link href="/unlocking-doors-through-investment-lending" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/unlocking-doors-through-investment-lending')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Investment Loans')}
                                                                </Typography>
                                                            </Link>
                                                            <Link href="/expat-finance-simplified-for-australian-property" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/expat-finance-simplified-for-australian-property')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Expat Finance')}
                                                                </Typography>
                                                            </Link>
                                                            <Link href="/financing-smsf-property-investment" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/financing-smsf-property-investment')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('SMSF Property')}
                                                                </Typography>
                                                            </Link>
                                                        </Box>
                                                        <Box
                                                            className={
                                                                styles.dropdownColumn
                                                            }
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                    marginBottom: "6px",
                                                                    color: "#33A8E4",
                                                                }}
                                                            >
                                                                {t('Finance Business')}
                                                            </Typography>
                                                            <Link href="/finance-for-business-growth" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/finance-for-business-growth')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Business Loans')}
                                                                </Typography>
                                                            </Link>
                                                            <Link href="/commercial-loans" passHref>
                                                                <Typography
                                                                    className={`${styles.navLink} ${getSubmenuActiveClass('/commercial-loans')}`}
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: "bold",
                                                                    }}
                                                                    onClick={handleDrawerToggle}
                                                                >
                                                                    {t('Commercial Loans')}
                                                                </Typography>
                                                            </Link>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </li>

                                        <li style={{ display: 'flex' }}>
                                            <Link href="/about" passHref>
                                                <Tooltip
                                                    title={t('About')}
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'rgba(0, 0, 0, 0.8)',
                                                                fontSize: '1.5rem',
                                                                padding: '0.75rem 1.5rem',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'rgba(0, 0, 0, 0.8)',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                        className={`${hoveredItem === "about"
                                                            ? styles.hovered
                                                            : hoveredItem
                                                                ? styles.lighter
                                                                : ""
                                                            } ${isScrolled
                                                                ? styles.scrolled
                                                                : styles.notScrolled
                                                            } ${getActiveClass('/about')}`}
                                                        onMouseEnter={() =>
                                                            setHoveredItem("about")
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredItem(null)
                                                        }
                                                        style={{
                                                            color: getLinkColor("about"),
                                                            '&:hover': {
                                                                color: getLinkHoverColor()
                                                            }
                                                        }}
                                                    >
                                                        {t('About')}
                                                    </Typography>
                                                </Tooltip>
                                            </Link>
                                        </li>

                                        <li style={{ display: 'flex' }}>
                                            <Box
                                                sx={{ position: "relative" }}
                                                onMouseEnter={() =>
                                                    handleMouseEnter("resources")
                                                }
                                                onMouseLeave={() =>
                                                    handleMouseLeave("resources")
                                                }
                                            >
                                                <Tooltip
                                                    title={t('Resources')}
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'rgba(0, 0, 0, 0.8)',
                                                                fontSize: '1.5rem',
                                                                padding: '0.75rem 1.5rem',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'rgba(0, 0, 0, 0.8)',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            // gap: 1,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                        className={`${hoveredItem === "resources"
                                                            ? styles.hovered
                                                            : hoveredItem
                                                                ? styles.lighter
                                                                : ""
                                                            } ${isScrolled
                                                                ? styles.scrolled
                                                                : styles.notScrolled
                                                            }`}
                                                        onMouseEnter={() =>
                                                            setHoveredItem("resources")
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredItem(null)
                                                        }
                                                        style={{
                                                            color: getLinkColor("resources"),
                                                            '&:hover': {
                                                                color: getLinkHoverColor()
                                                            }
                                                        }}
                                                    >
                                                        {t('Resources')} <KeyboardArrowDownIcon sx={{ color: getLinkColor("resources"), height: "2.25rem", width: "2.25rem" }} />
                                                    </Typography>
                                                </Tooltip>
                                                {dropdownOpen === "resources" && (
                                                    <Box className={styles.dropdown}>
                                                        <Link href="/videos" passHref>
                                                            <Typography
                                                                className={`${styles.navLink} ${getSubmenuActiveClass('/videos')}`}
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                }}
                                                                onClick={handleDrawerToggle}
                                                            >
                                                                {t('Videos')}
                                                            </Typography>
                                                        </Link>
                                                        <Link href="/blogs" passHref>
                                                            <Typography
                                                                className={`${styles.navLink} ${getSubmenuActiveClass('/blogs')}`}
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                }}
                                                                onClick={handleDrawerToggle}
                                                            >
                                                                {t('Blogs')}
                                                            </Typography>
                                                        </Link>
                                                        <Link href="/faq" passHref>
                                                            <Typography
                                                                className={`${styles.navLink} ${getSubmenuActiveClass('/faq')}`}
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                }}
                                                                onClick={handleDrawerToggle}
                                                            >
                                                                {t('FAQ')}
                                                            </Typography>
                                                        </Link>
                                                    </Box>
                                                )}
                                            </Box>
                                        </li>

                                        <li style={{ display: 'flex' }}>
                                            <Link href="/loan-calculators" passHref>
                                                <Tooltip
                                                    title={t('Loan Calculators')}
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'rgba(0, 0, 0, 0.8)',
                                                                fontSize: '1.5rem',
                                                                padding: '0.75rem 1.5rem',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'rgba(0, 0, 0, 0.8)',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                        className={`${hoveredItem === "loanCalculators"
                                                            ? styles.hovered
                                                            : hoveredItem
                                                                ? styles.lighter
                                                                : ""
                                                            } ${isScrolled
                                                                ? styles.scrolled
                                                                : styles.notScrolled
                                                            } ${getActiveClass('/loan-calculators')}`}
                                                        onMouseEnter={() =>
                                                            setHoveredItem("loanCalculators")
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredItem(null)
                                                        }
                                                        style={{
                                                            color: getLinkColor("loanCalculators"),
                                                            '&:hover': {
                                                                color: getLinkHoverColor()
                                                            }
                                                        }}
                                                    >
                                                        {t('Loan Calculators')}
                                                    </Typography>
                                                </Tooltip>
                                            </Link>
                                        </li>

                                        <li style={{ display: 'flex' }}>
                                            <Box
                                                sx={{ position: "relative" }}
                                                onMouseEnter={() =>
                                                    handleMouseEnter("locations")
                                                }
                                                onMouseLeave={() =>
                                                    handleMouseLeave("locations")
                                                }
                                            >
                                                <Tooltip
                                                    title={t('Locations')}
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'rgba(0, 0, 0, 0.8)',
                                                                fontSize: '1.5rem',
                                                                padding: '0.75rem 1.5rem',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'rgba(0, 0, 0, 0.8)',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            // gap: 1,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                        className={`${hoveredItem === "locations"
                                                            ? styles.hovered
                                                            : hoveredItem
                                                                ? styles.lighter
                                                                : ""
                                                            } ${isScrolled
                                                                ? styles.scrolled
                                                                : styles.notScrolled
                                                            }`}
                                                        style={{
                                                            color: getLinkColor("locations"),
                                                            '&:hover': {
                                                                color: getLinkHoverColor()
                                                            }
                                                        }}
                                                    >
                                                        {t('Locations')} <KeyboardArrowDownIcon sx={{ color: getLinkColor("locations"), height: "2.25rem", width: "2.25rem" }} />
                                                    </Typography>
                                                </Tooltip>
                                                {dropdownOpen === "locations" && (
                                                    <Box className={styles.dropdown}>
                                                        <Link href="/mortgage-broker-malvern" passHref>
                                                            <Typography
                                                                className={`${styles.navLink} ${getSubmenuActiveClass('/mortgage-broker-malvern')}`}
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                }}
                                                                onClick={handleDrawerToggle}
                                                            >
                                                                {t('Malvern')}
                                                            </Typography>
                                                        </Link>
                                                        <Link href="/leading-coffs-harbour-finance-brokers" passHref>
                                                            <Typography
                                                                className={`${styles.navLink} ${getSubmenuActiveClass('/leading-coffs-harbour-finance-brokers')}`}
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: "bold",
                                                                }}
                                                                onClick={handleDrawerToggle}
                                                            >
                                                                {t('Coffs Harbour')}
                                                            </Typography>
                                                        </Link>
                                                    </Box>
                                                )}
                                            </Box>
                                        </li>

                                        <li style={{ display: 'flex' }}>
                                            <Link href="/contact" passHref>
                                                <Tooltip
                                                    title={t('Contact')}
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                bgcolor: 'rgba(0, 0, 0, 0.8)',
                                                                fontSize: '1.5rem',
                                                                padding: '0.75rem 1.5rem',
                                                                '& .MuiTooltip-arrow': {
                                                                    color: 'rgba(0, 0, 0, 0.8)',
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            fontSize: 14,
                                                            fontWeight: 600,
                                                        }}
                                                        className={`${hoveredItem === "contacts"
                                                            ? styles.hovered
                                                            : hoveredItem
                                                                ? styles.lighter
                                                                : ""
                                                            } ${isScrolled
                                                                ? styles.scrolled
                                                                : styles.notScrolled
                                                            } ${getActiveClass('/contact')}`}
                                                        onMouseEnter={() =>
                                                            setHoveredItem("contacts")
                                                        }
                                                        onMouseLeave={() =>
                                                            setHoveredItem(null)
                                                        }
                                                        style={{
                                                            color: getLinkColor("contacts"),
                                                            '&:hover': {
                                                                color: getLinkHoverColor()
                                                            }
                                                        }}
                                                    >
                                                        {t('Contact')}
                                                    </Typography>
                                                </Tooltip>
                                            </Link>
                                        </li>
                                    </ul>
                                </nav>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                padding: "1.5rem 0"
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                    justifyContent: "space-between",
                                }}
                            >
                                <Link href="/" passHref>
                                    <Image
                                        src={getLogoSource()}
                                        width={140}
                                        height={45}
                                        alt="keyChoiceLendingLogo"
                                        aria-hidden="false"
                                        role="img"
                                    />
                                </Link>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        position: "relative",
                                    }}
                                >
                                    {!searchClicked ? (
                                        <SearchIcon
                                            className={styles.searchIcon}
                                            onClick={() => setSearchClicked(true)}
                                            sx={{ color: getTextColor() }}
                                        />
                                    ) : (
                                        <Box className={`${styles.closeDropdown} ${styles.active}`}>
                                            <CloseIcon
                                                className={styles.closeIcon}
                                                onClick={() => {
                                                    setSearchClicked(false);
                                                    setSearchInputValue("");
                                                }}
                                            />
                                            <Box className={styles.searchBar}>
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    value={searchInputValue}
                                                    onChange={(e) => setSearchInputValue(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                                                />
                                                <SearchIcon
                                                    className={styles.searchIcon}
                                                    onClick={(e) => handleSearchSubmit(e)}
                                                    sx={{ color: "black" }}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                    <IconButton
                                        color="inherit"
                                        aria-label="open drawer"
                                        edge="start"
                                        onClick={handleDrawerToggle}
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '4rem'
                                            }
                                        }}
                                    >
                                        {mobileOpen ? (
                                            <CloseIcon sx={{ height: "4rem", width: "4rem" }} />
                                        ) : (
                                            <MenuIcon
                                                sx={{
                                                    height: "4rem",
                                                    width: "4rem"
                                                }} />
                                        )}
                                    </IconButton>
                                </Box>
                            </Box>
                            {/* Mobile Menu */}
                            <Box
                                className={styles.mobileDropDown}
                                sx={{
                                    display: mobileOpen ? "flex" : "none",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                    onClick={() =>
                                        handleMobileSubmenuToggle(
                                            "solutions"
                                        )
                                    }
                                >
                                    <Link href="/solutions" passHref>
                                        <Typography
                                            className={`${styles.simpleHoveredLink} ${mobileSubmenuOpen === "solutions" ? styles.active : ""} ${getActiveClass('/solutions')}`}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: "#000",
                                            }}
                                            onClick={handleDrawerToggle}
                                        >
                                            {t('Solutions')}
                                        </Typography>
                                    </Link>
                                    {mobileSubmenuOpen === "solutions" ? (
                                        <KeyboardArrowUpIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    ) : (
                                        <KeyboardArrowDownIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    )}
                                </Box>
                                {mobileSubmenuOpen === "solutions" && (
                                    <Box
                                        sx={{
                                            pl: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    marginBottom: "6px",
                                                    color: "#33A8E4",
                                                }}
                                            >
                                                {t('Buy or Refinance a Home')}
                                            </Typography>
                                            <Link href="/home-loan-for-your-dream-home" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/home-loan-for-your-dream-home')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Home Loans')}
                                                </Typography>
                                            </Link>
                                            <Link href="/the-power-of-refinancing" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/the-power-of-refinancing')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Refinance')}
                                                </Typography>
                                            </Link>
                                            <Link href="/reverse-mortgage" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/reverse-mortgage')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Reverse Mortgage')}
                                                </Typography>
                                            </Link>
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    marginBottom: "6px",
                                                    color: "#33A8E4",
                                                }}
                                            >
                                                {t('Invest in Property')}
                                            </Typography>
                                            <Link href="/unlocking-doors-through-investment-lending" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/unlocking-doors-through-investment-lending')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Investment Loans')}
                                                </Typography>
                                            </Link>
                                            <Link href="/expat-finance-simplified-for-australian-property" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/expat-finance-simplified-for-australian-property')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Expat Finance')}
                                                </Typography>
                                            </Link>
                                            <Link href="/financing-smsf-property-investment" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/financing-smsf-property-investment')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('SMSF Property')}
                                                </Typography>
                                            </Link>
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    marginBottom: "6px",
                                                    color: "#33A8E4",
                                                }}
                                            >
                                                {t('Finance Business')}
                                            </Typography>
                                            <Link href="/finance-for-business-growth" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/finance-for-business-growth')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Business Loans')}
                                                </Typography>
                                            </Link>
                                            <Link href="/commercial-loans" passHref>
                                                <Typography
                                                    className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/commercial-loans')} ${styles.marginBbottom}`}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: "bold",
                                                        color: "#000",
                                                        mb: 0.5
                                                    }}
                                                    onClick={handleDrawerToggle}
                                                >
                                                    {t('Commercial Loans')}
                                                </Typography>
                                            </Link>
                                        </Box>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Link href="/about" passHref>
                                        <Typography
                                            className={`${styles.simpleHoveredLink} ${mobileSubmenuOpen === "about" ? styles.active : ""} ${getActiveClass('/about')}`}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: "#000",
                                            }}
                                            onClick={handleDrawerToggle}
                                        >
                                            {t('About')}
                                        </Typography>
                                    </Link>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                    onClick={() =>
                                        handleMobileSubmenuToggle(
                                            "resources"
                                        )
                                    }
                                >
                                    <Typography
                                        className={`${styles.simpleHoveredLink} ${mobileSubmenuOpen === "resources" ? styles.active : ""} ${getActiveClass('/resources')}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#000",
                                        }}
                                    >
                                        {t('Resources')}
                                    </Typography>
                                    {mobileSubmenuOpen === "resources" ? (
                                        <KeyboardArrowUpIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    ) : (
                                        <KeyboardArrowDownIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    )}
                                </Box>
                                {mobileSubmenuOpen === "resources" && (
                                    <Box
                                        sx={{
                                            pl: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        <Link href="/videos" passHref>
                                            <Typography
                                                className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/videos')}`}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                                onClick={handleDrawerToggle}
                                            >
                                                {t('Videos')}
                                            </Typography>
                                        </Link>
                                        <Link href="/blogs" passHref>
                                            <Typography
                                                className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/blogs')}`}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                                onClick={handleDrawerToggle}
                                            >
                                                {t('Blogs')}
                                            </Typography>
                                        </Link>
                                        <Link href="/faq" passHref>
                                            <Typography
                                                className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/faq')}`}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                                onClick={handleDrawerToggle}
                                            >
                                                {t('FAQ')}
                                            </Typography>
                                        </Link>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Link href="/loan-calculators" passHref>
                                        <Typography
                                            className={`${styles.simpleHoveredLink
                                                }  ${getActiveClass('/loan-calculators')}`}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: "#000",
                                            }}
                                            onClick={handleDrawerToggle}

                                        >
                                            {t('Loan Calculators')}
                                        </Typography>
                                    </Link>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                    onClick={() =>
                                        handleMobileSubmenuToggle(
                                            "locations"
                                        )
                                    }
                                >
                                    <Typography
                                        className={`${styles.simpleHoveredLink} ${mobileSubmenuOpen === "locations" ? styles.active : ""} ${getActiveClass('/locations')}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#000",
                                        }}
                                    >
                                        {t('Locations')}
                                    </Typography>
                                    {mobileSubmenuOpen === "locations" ? (
                                        <KeyboardArrowUpIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    ) : (
                                        <KeyboardArrowDownIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    )}
                                </Box>
                                {mobileSubmenuOpen === "locations" && (
                                    <Box
                                        sx={{
                                            pl: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        <Link href="/mortgage-broker-malvern" passHref>
                                            <Typography
                                                className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/mortgage-broker-malvern')}`}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                                onClick={handleDrawerToggle}
                                            >
                                                {t('Malvern')}
                                            </Typography>
                                        </Link>
                                        <Link href="/leading-coffs-harbour-finance-brokers" passHref>
                                            <Typography
                                                className={`${styles.simpleHoveredLink} ${getSubmenuActiveClass('/leading-coffs-harbour-finance-brokers')}`}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                                onClick={handleDrawerToggle}
                                            >
                                                {t('Coffs Harbour')}
                                            </Typography>
                                        </Link>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Link href="/contact" passHref>
                                        <Typography
                                            className={`${styles.simpleHoveredLink} ${mobileSubmenuOpen === "contacts" ? styles.active : ""} ${getActiveClass('/contact')}`}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                fontSize: 14,
                                                fontWeight: 600,
                                                color: "#000",
                                            }}
                                            onClick={handleDrawerToggle}
                                        >
                                            {t('Contact')}
                                        </Typography>
                                    </Link>
                                </Box>
                                {/* Language Selection */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 3,
                                        justifyContent: "space-between",
                                    }}
                                    onClick={() =>
                                        handleMobileSubmenuToggle(
                                            "language"
                                        )
                                    }
                                >
                                    <Typography
                                        className={`${styles.simpleHoveredLink} ${mobileSubmenuOpen === "language" ? styles.active : ""}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#000",
                                        }}
                                    >
                                        {t('Language')}
                                    </Typography>
                                    {mobileSubmenuOpen === "language" ? (
                                        <KeyboardArrowUpIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    ) : (
                                        <KeyboardArrowDownIcon
                                            className={
                                                styles.downArrowMobile
                                            }
                                            sx={{
                                                height: "1.25rem",
                                                width: "1.25rem"
                                            }}
                                        />
                                    )}
                                </Box>
                                {mobileSubmenuOpen === "language" && (
                                    <Box
                                        sx={{
                                            pl: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                cursor: "pointer",
                                                padding: "8px 0",
                                            }}
                                            onClick={() => {
                                                handleCountryChange({ target: { value: "australia" } });
                                                handleDrawerToggle();
                                            }}
                                        >
                                            <Image
                                                src={australiaFlag}
                                                width={20}
                                                height={20}
                                                alt="Australia Flag"
                                                aria-hidden="false"
                                                role="img"
                                            />
                                            <Typography
                                                className={selectedCountry === "australia" ? styles.active : ""}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                            >
                                                Australia
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                cursor: "pointer",
                                                padding: "8px 0",
                                            }}
                                            onClick={() => {
                                                handleCountryChange({ target: { value: "vietnam" } });
                                                handleDrawerToggle();
                                            }}
                                        >
                                            <Image
                                                src={vietnamFlag}
                                                width={20}
                                                height={20}
                                                alt="Tiếng Việt Flag"
                                                aria-hidden="false"
                                                role="img"
                                            />
                                            <Typography
                                                className={selectedCountry === "vietnam" ? styles.active : ""}
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: "bold",
                                                    color: "#000",
                                                }}
                                            >
                                                Tiếng Việt
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </div>
        </AppBar>
    );
};
export default NavBar;
